export interface DatadogRumConfig {
  readonly version: 1
  readonly applicationId: string
  readonly clientToken: string
  readonly site: 'datadoghq.com'
  readonly service: string
  readonly env: string
  readonly proxyUrl: string
  readonly sessionSampleRate: number
  readonly profilingSampleRate: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

// The proxy host varies by environment and sibling domain, so only the scheme and credentials are constrained.
function isValidProxyUrl(proxyUrl: unknown, documentOrigin?: string): proxyUrl is string {
  if (typeof proxyUrl !== 'string' || !proxyUrl.trim()) return false

  try {
    const url = new URL(proxyUrl, documentOrigin ?? 'https://example.invalid')
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:'
    return isHttp && !url.username && !url.password
  } catch {
    return false
  }
}

export function isValidDatadogRumConfig(config: unknown, documentOrigin?: string): config is DatadogRumConfig {
  return Boolean(
    isRecord(config) &&
    config.version === 1 &&
    typeof config.applicationId === 'string' &&
    config.applicationId &&
    typeof config.clientToken === 'string' &&
    config.clientToken &&
    config.site === 'datadoghq.com' &&
    typeof config.service === 'string' &&
    config.service &&
    typeof config.env === 'string' &&
    config.env &&
    isValidProxyUrl(config.proxyUrl, documentOrigin) &&
    typeof config.sessionSampleRate === 'number' &&
    config.sessionSampleRate >= 0 &&
    config.sessionSampleRate <= 100 &&
    typeof config.profilingSampleRate === 'number' &&
    config.profilingSampleRate >= 0 &&
    config.profilingSampleRate <= 100,
  )
}

export function getDatadogRumConfig(root: Document = document): DatadogRumConfig | undefined {
  const configElement = root.getElementById('datadog-rum-config')
  if (!configElement) return undefined

  try {
    const config: unknown = JSON.parse(configElement.textContent || '')
    return isValidDatadogRumConfig(config, root.location.origin) ? config : undefined
  } catch {
    return undefined
  }
}
